import type { User } from "@/lib/api-types"

/**
 * Check if user is a platform admin
 */
export function isPlatformAdmin(user: User | null): boolean {
  if (!user) return false
  return user.is_platform_admin === true
}

/**
 * Check if user is a company admin for their current company
 * Checks multiple possible locations where the role might be stored
 */
export function isCompanyAdmin(user: User | null): boolean {
  if (!user) return false

  // Check all possible locations for company admin role
  if (user.company_role === "admin") return true
  if (user.current_company?.role === "admin") return true

  // Also check in companies array for current company
  if (user.companies && user.current_company) {
    const currentCompany = user.companies.find(
      c => c.company_id === user.current_company?.company_id
    )
    if (currentCompany?.role === "admin") return true
  }

  return false
}

/**
 * Check if user is either a platform admin OR company admin
 */
export function isCompanyOrPlatformAdmin(user: User | null): boolean {
  return isPlatformAdmin(user) || isCompanyAdmin(user)
}

/**
 * Check if user has app admin role in any app
 */
export function hasAppAdminRole(user: User | null): boolean {
  if (!user) return false

  // Check current company's apps
  if (user.current_company?.apps) {
    const hasAdmin = user.current_company.apps.some(app => app.role === "admin")
    if (hasAdmin) return true
  }

  // Check all companies' apps
  if (user.companies) {
    for (const company of user.companies) {
      if (company.apps?.some(app => app.role === "admin")) {
        return true
      }
    }
  }

  return false
}

/**
 * Check if user has app manager role in any app
 */
export function hasAppManagerRole(user: User | null): boolean {
  if (!user) return false

  // Check current company's apps
  if (user.current_company?.apps) {
    const hasManager = user.current_company.apps.some(app => app.role === "manager")
    if (hasManager) return true
  }

  // Check all companies' apps
  if (user.companies) {
    for (const company of user.companies) {
      if (company.apps?.some(app => app.role === "manager")) {
        return true
      }
    }
  }

  return false
}

/**
 * Check if user has app admin or manager role in any app
 */
export function hasAppAdminOrManagerRole(user: User | null): boolean {
  return hasAppAdminRole(user) || hasAppManagerRole(user)
}

/**
 * Check if a user can access the admin section
 * Returns true if user is:
 * - Platform admin
 * - Company admin
 * - Has admin or manager role in at least one app
 */
export function canAccessAdmin(user: User | null): boolean {
  if (!user) return false

  // Use the new centralized helpers
  if (isPlatformAdmin(user)) return true
  if (isCompanyAdmin(user)) return true
  if (hasAppAdminOrManagerRole(user)) return true

  return false
}

/**
 * Check if user can view billing information
 * Only platform admins and company admins can view billing
 */
export function canViewBilling(user: User | null): boolean {
  return isCompanyOrPlatformAdmin(user)
}

/**
 * Check if user can manage company settings
 * Only platform admins and company admins can manage company settings
 */
export function canManageCompany(user: User | null): boolean {
  return isCompanyOrPlatformAdmin(user)
}

/**
 * Check if user can manage users
 * Platform admins, company admins, and app admins can manage users
 */
export function canManageUsers(user: User | null): boolean {
  return isCompanyOrPlatformAdmin(user) || hasAppAdminRole(user)
}